import { Suspense, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { ListChecksIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import {
  type BuyerLeadDnDColumnFragment,
  type DevelopmentBuyerLeadsActionToolbarFragment,
  type EmailTemplateFormListingFragment,
  type UserCard_UserFragment,
} from '@graphql/__generated__/graphql';
import { BulkActionsMenu } from '@modules/_shared/components/buyer-lead/BulkActionsMenu';
import { BuyerLeadMoveToColumnDialog } from '@modules/_shared/components/buyer-lead/BuyerLeadMoveToColumnDialog';
import { getEmailedBuyerLeadIdsInStage } from '@modules/_shared/components/buyer-lead/buyerLeadRefresh';
import {
  type BuyerLeadKanbanStageOption,
  getBuyerLeadEmailRecipientStatus,
  getBuyerLeadEmailRecipients,
  getBuyerLeadParentGlobalIds,
} from '@modules/_shared/components/buyer-lead/buyerLeadUtils';
import {
  parseTemplateSuggestions,
  toSuggestionLanguage,
} from '@modules/_shared/components/email-form/contextSuggestions';
import { EmailFormDialog } from '@modules/_shared/components/email-form/EmailFormDialog';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SearchInput } from '@modules/_shared/components/search-input/SearchInput';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { useMatchedBuyerLeadEmailTemplate } from '@modules/_shared/utils/matchedBuyerLeadEmailTemplate';
import { useLocale } from '@shared/hooks/locale';

type BuyerLeadActionToolbarProps = React.PropsWithChildren<{
  buyerLeads: BuyerLeadDnDColumnFragment[];
  parent:
    | EmailTemplateFormListingFragment
    | DevelopmentBuyerLeadsActionToolbarFragment;
  readOnly?: boolean;
  buyerLeadsCount?: number;
  onFilterChange: (value: string) => void;
  selectedLeads: string[];
  onReset: () => void;
  columns: BuyerLeadKanbanStageOption[];
  forceSelectionMode: boolean;
  toggleSelectionMode: () => void;
  matchedBulkEmailStageId?: string;
  onEmailCampaignStarted?: (buyerLeadIds: string[]) => void;
}>;

// Use a big value for order_nr and user_order_nr so that the DB trigger can set them to the bottom of the column
export const UPDATE_BUYER_LEADS_STAGE_MULTIPLE = gql(/* GraphQL */ `
  mutation updateBuyerLeadsStageMultiple(
    $buyerLeadIds: [uuid!]!
    $stageId: uuid!
  ) {
    update_buyer_leads(
      where: { id: { _in: $buyerLeadIds } }
      _set: { stage_id: $stageId }
    ) {
      returning {
        id
        stage_id
        lead_stage {
          id
          status
        }
        created_at
        updated_at
      }
    }
  }
`);

export const DEVELOPMENT_BUYER_LEADS_ACTION_TOOLBAR_FRAGMENT =
  gql(/* GraphQL */ `
    fragment DevelopmentBuyerLeadsActionToolbar on developments {
      id
      broker {
        ...EmailUser
      }
      lots(where: { property: { route: { _is_null: false } } }, limit: 1) {
        property {
          ...EmailProperty
          property_images(order_by: { order_nr: asc }, limit: 1) {
            id
            image {
              id
              url
            }
          }
        }
      }
    }
  `);

export const GET_BUYER_LEAD_TOOLBAR_SUGGESTIONS = gql(/* GraphQL */ `
  query GetBuyerLeadToolbarSuggestions(
    $senderId: uuid
    $recipients: [uuid!]
    $listingId: uuid
    $developmentId: uuid
    $language: GetEmailTemplateSuggestionsLanguage
  ) {
    get_email_template_suggestions(
      senderId: $senderId
      recipients: $recipients
      listingId: $listingId
      developmentId: $developmentId
      language: $language
    ) {
      suggestions
    }
  }
`);

export const isDevelopment = (
  parent:
    | EmailTemplateFormListingFragment
    | DevelopmentBuyerLeadsActionToolbarFragment,
): parent is DevelopmentBuyerLeadsActionToolbarFragment => {
  return 'lots' in parent;
};

export type ActionableBuyerLead = {
  id: string;
  stage_id?: string | null;
  user: UserCard_UserFragment | null;
};

export const ActionsMenu: React.FC<{
  selectedLeads: ActionableBuyerLead[];
  columns: BuyerLeadKanbanStageOption[];
  onReset: () => void;
  parent:
    | EmailTemplateFormListingFragment
    | DevelopmentBuyerLeadsActionToolbarFragment;
  matchedBulkEmailStageId?: string;
  onEmailCampaignStarted?: (buyerLeadIds: string[]) => void;
}> = ({
  selectedLeads,
  columns,
  onReset,
  parent,
  matchedBulkEmailStageId,
  onEmailCampaignStarted,
}) => {
  const { t, language } = useLocale();
  const { me } = useAppData();
  const [modalOpened, setModalOpened] = useState<'email' | 'moveColumn' | null>(
    null,
  );

  // Keep buyers selected for a stage move, but prevent another send while
  // the campaign confirmation is still visible.
  const [campaignSentPending, setCampaignSentPending] = useState(false);

  const listingParent = isDevelopment(parent) ? null : parent;

  const allSelectedAreMatched =
    matchedBulkEmailStageId != null &&
    listingParent != null &&
    selectedLeads.length > 0 &&
    selectedLeads.every(lead => lead.stage_id === matchedBulkEmailStageId);

  const { data: suggestionsData, loading: suggestionsLoading } = useQuery(
    GET_BUYER_LEAD_TOOLBAR_SUGGESTIONS,
    {
      variables: {
        senderId: me?.id,
        recipients: [],
        ...(isDevelopment(parent)
          ? { developmentId: parent.id }
          : { listingId: parent.id }),
        language: toSuggestionLanguage(language),
      },
      skip: parent?.id == null || me?.id == null,
    },
  );

  const templateSuggestions = useMemo(
    () =>
      parseTemplateSuggestions(
        suggestionsData?.get_email_template_suggestions?.suggestions,
      ),
    [suggestionsData],
  );

  const {
    emailTemplate: matchedBulkEmailDefaults,
    hasLoadedEmailTemplate: hasLoadedMatchedBulkEmailDefaults,
    loading: matchedBulkEmailDefaultsLoading,
    error: matchedBulkEmailDefaultsError,
  } = useMatchedBuyerLeadEmailTemplate({
    listing: listingParent,
    language,
    t,
    skip: !allSelectedAreMatched,
  });

  const initialRecipients = useMemo(
    () => getBuyerLeadEmailRecipients(selectedLeads, lead => lead.user),
    [selectedLeads],
  );

  const emailRecipientStatus = getBuyerLeadEmailRecipientStatus(
    selectedLeads.length,
    initialRecipients.length,
    t,
  );

  const parentGlobalIds = useMemo(
    () => getBuyerLeadParentGlobalIds(selectedLeads),
    [selectedLeads],
  );

  const [updateMultipleBuyerLeads] = useMutation(
    UPDATE_BUYER_LEADS_STAGE_MULTIPLE,
  );

  return (
    <>
      <MutationErrorSnackbar error={matchedBulkEmailDefaultsError ?? null} />
      <BulkActionsMenu
        disabled={selectedLeads.length === 0}
        emailDisabled={
          campaignSentPending ||
          initialRecipients.length === 0 ||
          suggestionsLoading ||
          (allSelectedAreMatched && !hasLoadedMatchedBulkEmailDefaults)
        }
        emailTooltipTitle={
          matchedBulkEmailDefaultsLoading
            ? t('Loading...')
            : matchedBulkEmailDefaultsError != null
            ? t('An error occurred. Please try again.')
            : emailRecipientStatus.tooltipTitle
        }
        emailTooltipDisabled={
          !matchedBulkEmailDefaultsLoading &&
          matchedBulkEmailDefaultsError == null &&
          !emailRecipientStatus.hasMissingEmailRecipients
        }
        showEmailWarning={emailRecipientStatus.hasMissingEmailRecipients}
        onSendEmail={() => setModalOpened('email')}
        onMoveToColumn={() => setModalOpened('moveColumn')}
      />
      <BuyerLeadMoveToColumnDialog
        columns={columns}
        selectedLeadIds={selectedLeads.map(lead => lead.id)}
        opened={modalOpened === 'moveColumn'}
        onClose={() => setModalOpened(null)}
        onReset={onReset}
        updateMultipleBuyerLeads={updateMultipleBuyerLeads}
      />
      <EmailFormDialog
        initialRecipients={initialRecipients}
        templateSuggestions={templateSuggestions}
        parentGlobalIds={parentGlobalIds}
        open={
          modalOpened === 'email' &&
          (!allSelectedAreMatched || hasLoadedMatchedBulkEmailDefaults)
        }
        onClose={() => setModalOpened(null)}
        campaignMode={true}
        snippetLotId={isDevelopment(parent) ? null : parent.id}
        snippetDevelopmentId={isDevelopment(parent) ? parent.id : null}
        initialSubject={matchedBulkEmailDefaults?.subject.toString('plaintext')}
        initialBodyValue={matchedBulkEmailDefaults?.text}
        onEmailSent={(_, sentRecipients) => {
          setCampaignSentPending(true);
          onEmailCampaignStarted?.(
            getEmailedBuyerLeadIdsInStage(
              selectedLeads,
              sentRecipients,
              matchedBulkEmailStageId,
            ),
          );
        }}
        onCampaignSentDismissed={() => {
          setCampaignSentPending(false);
          emitFeedRefresh();
        }}
      />
    </>
  );
};

export const BuyerLeadActionToolbar: React.FC<BuyerLeadActionToolbarProps> = ({
  buyerLeads,
  parent,
  readOnly = false,
  onFilterChange,
  buyerLeadsCount,
  selectedLeads,
  onReset,
  columns,
  forceSelectionMode,
  toggleSelectionMode,
  matchedBulkEmailStageId,
  onEmailCampaignStarted,
  children,
}) => {
  const { t } = useLocale();
  const { breakpoints } = useTheme();

  const selectionInProgress = forceSelectionMode || selectedLeads.length > 0;
  const selectedLeadsData = useMemo(() => {
    return buyerLeads.filter(lead => selectedLeads.includes(lead.id)) ?? [];
  }, [selectedLeads, buyerLeads]);

  return (
    <>
      <Stack
        direction="row"
        spacing={{ xs: 0, sm: 2 }}
        sx={{
          px: 2,
          py: 1,
          alignItems: 'center',
          justifyContent: 'end',
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant={selectionInProgress ? 'body1' : 'h6'}
          sx={{
            flex: 1,
            whiteSpace: 'nowrap',

            [breakpoints.down('sm')]: {
              order: 1,
            },
          }}
        >
          {selectionInProgress
            ? t('{{count}} Buyer leads selected', {
                count: selectedLeads.length,
              })
            : t('Buyer leads ({{leadsCount}})', {
                leadsCount: buyerLeadsCount ?? 0,
              })}
        </Typography>
        <Box
          sx={{
            [breakpoints.down('sm')]: {
              mt: '8px !important',
              order: 3,
              width: '100%',
            },
          }}
        >
          <SearchInput onValueChanged={onFilterChange} fullWidth />
        </Box>
        {selectionInProgress ? (
          <Stack
            spacing={2}
            direction="row"
            sx={{
              alignItems: 'center',

              [breakpoints.down('sm')]: {
                order: 2,
              },
            }}
          >
            <Button variant="text" disableElevation onClick={onReset}>
              {t('Cancel')}
            </Button>
            <Suspense fallback={null}>
              <ActionsMenu
                selectedLeads={selectedLeadsData}
                columns={columns}
                onReset={onReset}
                parent={parent}
                matchedBulkEmailStageId={matchedBulkEmailStageId}
                onEmailCampaignStarted={onEmailCampaignStarted}
              />
            </Suspense>
          </Stack>
        ) : (
          <>
            {!readOnly && (
              <>
                <Button
                  variant="text"
                  startIcon={<ListChecksIcon />}
                  onClick={() => {
                    toggleSelectionMode();
                  }}
                  disableElevation
                  sx={{
                    [breakpoints.down('sm')]: {
                      order: 2,
                    },
                  }}
                >
                  {t('Select')}
                </Button>
                {children}
              </>
            )}
          </>
        )}
      </Stack>
    </>
  );
};
